// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: phone sheet for one note on the "What Konvo knows" page: the text to change, where it
// sits (topic), where it came from, Save and Forget.

import {forgetKnowsNote, updateKnowsNote} from '@app/features/konvo_knows/components/KnowsActions';
import {M, noteOriginText, TOPIC_DESCRIPTORS} from '@app/features/konvo_knows/components/KnowsMessages';
import {SourceIcon} from '@app/features/konvo_knows/components/KnowsNoteRow';
import styles from '@app/features/konvo_knows/components/KonvoKnows.module.css';
import {displayTopic, KNOWS_TOPICS, type KnowsNote, type KnowsTopic} from '@app/features/konvo_knows/utils/KnowsModel';
import {BottomSheet} from '@app/features/ui/bottom_sheet/BottomSheet';
import {TrashIcon} from '@app/features/ui/icons/Icons';
import {ActionButton, Chip} from '@app/features/ui/konvo_controls/KonvoControls';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useState} from 'react';

export const KnowsNoteSheet = observer(({note, onClose}: {note: KnowsNote; onClose: () => void}) => {
	const {i18n} = useLingui();
	const [text, setText] = useState(note.text);
	const [topic, setTopic] = useState<KnowsTopic>(displayTopic(note));
	const [busy, setBusy] = useState(false);
	const save = async () => {
		if (!text.trim()) return;
		setBusy(true);
		const ok = await updateKnowsNote(i18n, note, {text, topic});
		setBusy(false);
		if (ok) onClose();
	};
	return (
		<BottomSheet
			isOpen
			onClose={onClose}
			snapPoints={[0, 1]}
			initialSnap={1}
			fitContent
			pinToBottom
			title={i18n._(M.noteSheetTitle)}
			surface="primary"
		>
			<div className={styles.sheet} data-flx="konvo-knows.note-sheet">
				<textarea
					className={styles.sheetInput}
					value={text}
					rows={3}
					maxLength={300}
					aria-label={i18n._(M.change)}
					onChange={(event) => setText(event.target.value.replace(/\n/g, ' '))}
					data-flx="konvo-knows.note-sheet.text"
				/>
				<div className={styles.sheetLabel}>{i18n._(M.topicLabel)}</div>
				<div className={styles.sheetTopics} role="radiogroup" aria-label={i18n._(M.topicLabel)}>
					{KNOWS_TOPICS.map((candidate) => (
						<Chip
							key={candidate}
							selected={candidate === topic}
							role="radio"
							aria-checked={candidate === topic}
							onClick={() => setTopic(candidate)}
							data-flx={`konvo-knows.note-sheet.topic.${candidate}`}
						>
							{i18n._(TOPIC_DESCRIPTORS[candidate])}
						</Chip>
					))}
				</div>
				<div className={styles.sheetOrigin}>
					<SourceIcon note={note} size={13} />
					{noteOriginText(i18n, note)}
				</div>
				<div className={styles.sheetActions}>
					<ActionButton
						tone="danger"
						icon={<TrashIcon size={16} />}
						disabled={busy}
						onClick={() => {
							onClose();
							void forgetKnowsNote(i18n, note);
						}}
						data-flx="konvo-knows.note-sheet.forget"
					>
						{i18n._(M.forget)}
					</ActionButton>
					<ActionButton
						tone="primary"
						disabled={busy || !text.trim()}
						onClick={() => void save()}
						data-flx="konvo-knows.note-sheet.save"
					>
						{i18n._(M.save)}
					</ActionButton>
				</div>
			</div>
		</BottomSheet>
	);
});
